import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaPlusCircle,
  FaCalendarAlt,
  FaChevronRight,
  FaFolderOpen,
  FaBullhorn,
  FaCheckCircle,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/* ----------------------------------------------------------------------------
   CONFIG
-----------------------------------------------------------------------------*/
const ABSOLUTE_HOST = "https://dev.sliitleo.org";

/* ----------------------------------------------------------------------------
   HELPERS
-----------------------------------------------------------------------------*/
function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    if (raw?.image && typeof raw.image === "string" && !raw.image.startsWith("http")) {
      raw.image = `${ABSOLUTE_HOST}${raw.image.startsWith("/") ? "" : "/"}${raw.image}`;
    }
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "User", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}
async function parseJsonSafe(res) {
  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(t.slice(0, 400));
  }
}

/* ----------------------------------------------------------------------------
   SKELETON
-----------------------------------------------------------------------------*/
function ProjectSkeletonCard() {
  return (
    <div className="rounded-2xl bg-[#0b1536]/80 p-4 backdrop-blur-sm shadow-[0_10px_30px_-18px_rgba(0,0,0,.7)]">
      <div className="h-4 w-36 bg-white/10 rounded mb-2 animate-pulse" />
      <div className="h-3 w-28 bg-white/10 rounded mb-3 animate-pulse" />
      <div className="h-9 w-full bg-white/10 rounded animate-pulse" />
    </div>
  );
}

/* ----------------------------------------------------------------------------
   STATUS STYLES
-----------------------------------------------------------------------------*/
function statusStyle(status = "") {
  const s = status.toLowerCase();
  if (/(done|complete|completed|success|approved)/i.test(status))
    return "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30";
  if (/(pending|planned|draft|on hold|waiting)/i.test(status))
    return "bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30";
  if (/(blocked|failed|cancel|rejected|issue)/i.test(status))
    return "bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/30";
  return "bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30";
}

/* ----------------------------------------------------------------------------
   EVENT HELPERS (support ISO `start` or DB `event_date`/`start_time`)
-----------------------------------------------------------------------------*/
function getEventStartDate(ev) {
  if (ev?.start) {
    const d = new Date(String(ev.start));
    if (!isNaN(d)) return d;
  }
  if (ev?.event_date) {
    const t = ev.start_time || "00:00:00";
    const d = new Date(`${String(ev.event_date).trim()}T${t}`);
    if (!isNaN(d)) return d;
  }
  if (ev?.end) {
    const d = new Date(String(ev.end));
    if (!isNaN(d)) return d;
  }
  return null;
}
function getEventEndTimeStr(ev) {
  if (ev?.end && /T\d{2}:\d{2}/.test(ev.end)) {
    const d = new Date(String(ev.end));
    if (!isNaN(d)) return d.toTimeString().slice(0, 5);
  }
  if (ev?.end_time) return String(ev.end_time).slice(0, 5);
  return "";
}
function formatEventDateTimeFromEvent(ev) {
  const start = getEventStartDate(ev);
  if (!start) return "";
  const day = start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const st = start.toTimeString().slice(0, 5);
  const et = getEventEndTimeStr(ev);
  if (st && et) return `${day} • ${st}–${et}`;
  if (st) return `${day} • ${st}`;
  return day;
}
function formatEventDateLabelFromEvent(ev) {
  const d = getEventStartDate(ev);
  if (!d) return { day: "", mon: "" };
  return {
    day: d.toLocaleDateString([], { day: "2-digit" }),
    mon: d.toLocaleDateString([], { month: "short" }),
  };
}
function cleanDesc(s = "") {
  return String(s || "").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}

/* ----------------------------------------------------------------------------
   MAIN
-----------------------------------------------------------------------------*/
export default function Dashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(readUserFromStorage());
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(() => Date.now());

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [errorProjects, setErrorProjects] = useState("");

  // Events (Announcements -> Latest Calendar Events)
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const avatarSrc = useMemo(() => {
    const base = user?.image || "/user.png";
    const sep = base.includes("?") ? "&" : "?";
    const ver = user?._avatarTs || avatarVersion;
    return `${base}${base === "/user.png" ? "" : `${sep}v=${ver}`}`;
  }, [user, avatarVersion]);

  const refreshUserFromAPI = useCallback(async () => {
    const current = readUserFromStorage();
    setUser(current);
    if (!current?.SLIITID) return;

    try {
      setLoadingAvatar(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_profile.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SLIITID: current.SLIITID }),
      });
      const data = await parseJsonSafe(res);

      if (data?.success && data.profile) {
        const next = {
          ...current,
          name: data.profile.name || current.name,
          address: data.profile.Address ?? current.address,
          contactno: data.profile.ContactNo ?? current.contactno,
        };
        const rawImg = data.profile.image || data.profile.ProfileLink;
        if (rawImg) {
          const absolute = rawImg.startsWith("http")
            ? rawImg
            : `${ABSOLUTE_HOST}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`;
          if (absolute !== current.image) {
            next.image = absolute;
            next._avatarTs = Date.now();
          }
        }
        localStorage.setItem("user", JSON.stringify(next));
        setUser(next);
        if (next._avatarTs) setAvatarVersion(next._avatarTs);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingAvatar(false);
    }
  }, []);

  const loadVisibleProjects = useCallback(async () => {
    setLoadingProjects(true);
    setErrorProjects("");
    try {
      const current = readUserFromStorage();
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SLIITID: current?.SLIITID }),
      });
      const data = await parseJsonSafe(res);

      if (data?.success && Array.isArray(data.projects)) {
        const sorted = [...data.projects].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        setProjects(sorted);
      } else {
        setProjects([]);
        if (data?.message) setErrorProjects(data.message);
      }
    } catch {
      setErrorProjects("Unable to fetch projects.");
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Load latest events (works with ISO start or DB fields)
  const loadLatestEvents = useCallback(async () => {
    setEventsError("");
    setLoadingEvents(true);
    try {
      const body = new URLSearchParams({ limit: "5", upcoming: "1" });
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_events.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body,
      });

      const parsed = await parseJsonSafe(res);

      let list =
        (parsed && Array.isArray(parsed.events) && parsed.events) ||
        (parsed && Array.isArray(parsed.data) && parsed.data) ||
        (parsed && Array.isArray(parsed.result) && parsed.result) ||
        (Array.isArray(parsed) ? parsed : []);

      const now = Date.now();
      list = list
        .map((ev) => {
          const d = getEventStartDate(ev);
          return { ...ev, _start: d ? d.getTime() : Number.MAX_SAFE_INTEGER };
        })
        .filter((ev) => ev._start >= now)
        .sort((a, b) => a._start - b._start)
        .slice(0, 5);

      setEvents(list);
      if (!list.length) setEventsError("");
    } catch {
      setEvents([]);
      setEventsError("Unable to fetch events.");
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    refreshUserFromAPI();
    loadVisibleProjects();
    loadLatestEvents();

    const onStorage = (e) => {
      if (e.key === "user") {
        const next = readUserFromStorage();
        setUser(next);
        setAvatarVersion(Date.now());
        loadVisibleProjects();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { first, last } = splitName(user.name || "");
  const isAdmin = /admin/i.test(user.role || "");
  const roleLabel = isAdmin ? "System Admin" : user.role || "Member";

  const total = projects.length;
  const active = projects.filter((p) => /progress|ongoing|review|testing/i.test(p?.status || "")).length;
  const pending = projects.filter((p) => /pending|draft|planned/i.test(p?.status || "")).length;

  const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
  };

  const openProject = (id) => navigate(`/project/${id}`);

  return (
    <div className="relative flex min-h-screen flex-col lg:flex-row text-white bg-[#0b122a]">
      {/* Ambient background */}
      <div
        className="pointer-events-none fixed inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -10%, rgba(47,107,255,0.12), transparent 60%), radial-gradient(1000px 500px at 100% 10%, rgba(0,173,255,0.10), transparent 55%)",
        }}
      />
      <Sidebar />
      <Topbar user={user} />

      {/* Content container with max width for large screens */}
      <main className="relative w-full lg:ml-64 mt-14">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 py-6 max-w-7xl mx-auto">
          {/* GRID: Left content / Divider / Right rail */}
          <div className="grid grid-cols-12 gap-6">
            {/* LEFT — main column */}
            <div className="col-span-12 xl:col-span-7">
              {/* HERO */}
              <motion.div
  {...fadeUp}
  style={{
    borderRadius: 24,
    overflow: 'hidden',
    background: '#141a2a',
    padding: '28px',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 20px 80px -40px rgba(0,0,0,0.75)'
  }}
>
  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 24, flexWrap:'wrap' }}>
    {/* AVATAR */}
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div style={{
        borderRadius: '50%',
        padding: 3,
        background: '#0f1e55',
        border: '1px solid rgba(255,255,255,0.10)'
      }}>
        {loadingAvatar ? (
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)', animation: 'pulse 1.2s infinite'
          }}/>
        ) : (
          <img
            key={avatarSrc}
            src={avatarSrc}
            alt="User"
            style={{
              width: 80, height: 80,
              borderRadius: '50%',
              objectFit: 'cover',
              userSelect: 'none'
            }}
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = "/user.png";
              }
            }}
            draggable="false"
          />
        )}
      </div>

      {/* org logo dot */}
      <div style={{
        position:'absolute', top:-4, left:-4,
        width:20, height:20, borderRadius:'50%',
        background:'#0f1e55',
        border:'2px solid #0f1e55',
        display:'grid', placeItems:'center',
        overflow:'hidden'
      }}>
        <img src="/club.png" alt="Org" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}/>
      </div>

      {/* active status dot */}
      <div style={{
        position:'absolute', bottom:-4, right:-4,
        width:20, height:20, borderRadius:'50%',
        background:'#10b981',
        border:'2px solid #0f1e55',
        display:'grid', placeItems:'center',
        boxShadow:'0 0 14px rgba(16,185,129,.8)'
      }}>
        <FaCheckCircle style={{ color:'#fff', fontSize:11 }}/>
      </div>
    </div>

    {/* TEXTS */}
    <div style={{ flex:1, minWidth:0 }}>
      <p style={{ fontSize:12, letterSpacing:0.5, color:'rgba(255,255,255,0.65)', margin:0 }}>
        @ {roleLabel}
      </p>

      <h1 style={{
        margin:'6px 0 0 0',
        fontSize:'1.9rem',
        lineHeight:1.2,
        fontFamily:'"Inter", "SF Pro Display", system-ui, sans-serif',
        fontWeight:300, // light for "Welcome back,"
        color:'#ffffff'
      }}>
        Welcome back,&nbsp;
        <span style={{
          fontWeight:700, // bold for name
          color:'#ffffff'
        }}>
          {first} {last}
        </span>
      </h1>

      {/* ACTION BUTTONS */}
      <div style={{ marginTop:16, display:'flex', flexWrap:'wrap', gap:12 }}>
        <button
          onClick={() => navigate("/create-project")}
          style={{
            display:'inline-flex', alignItems:'center', gap:8,
            padding:'10px 16px',
            borderRadius:999,
            background:'#262c3c',
            border:'1px solid rgba(255,255,255,0.10)',
            cursor:'pointer',
            color:'#e6efff'
          }}
        >
          <span style={{
            display:'grid', placeItems:'center',
            width:24, height:24, borderRadius:'50%',
            background:'#4f46e5'
          }}>
            <FaPlusCircle />
          </span>
          <span style={{ fontSize:14 }}>Create Project</span>
        </button>

        <button
          onClick={() => navigate("/club-calander")}
          style={{
            display:'inline-flex', alignItems:'center', gap:8,
            padding:'10px 16px',
            borderRadius:999,
            background:'#262c3c',
            border:'1px solid rgba(255,255,255,0.10)',
            cursor:'pointer',
            color:'#e6efff'
          }}
        >
          <span style={{
            display:'grid', placeItems:'center',
            width:24, height:24, borderRadius:'50%',
            background:'#16a34a'
          }}>
            <FaCalendarAlt />
          </span>
          <span style={{ fontSize:14 }}>Project Calendar</span>
        </button>
      </div>

      {/* STATS */}
      <div style={{ marginTop:18, display:'flex', flexWrap:'wrap', gap:12 }}>
        {[{label:"Projects", value:total}, {label:"Active", value:active}, {label:"Pending", value:pending}]
          .map((s,i)=>(
            <div key={i}
              style={{
                flex:'0 0 auto',
                minWidth:70,
                borderRadius:12,
                background:'rgba(15,22,49,0.85)',
                padding:'8px 14px',
                textAlign:'center',
                border:'1px solid rgba(255,255,255,0.10)',
                boxShadow:'0 10px 30px -18px rgba(0,0,0,.6)'
              }}
            >
              <div style={{ fontSize:16, fontWeight:700, color:'#fff', lineHeight:1 }}>{s.value}</div>
              <div style={{ fontSize:10, letterSpacing:0.5, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
      </div>
    </div>
  </div>
</motion.div>


              {/* Announcements -> Latest Calendar Events (inline CSS, forced colors) */}
<div style={{ marginTop: 24 }}>
  <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between' }}>
    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, display:'flex', alignItems:'center', gap:8 }}>
      <FaBullhorn style={{ color:'#6d6aff' }} />
      <span>Latest <span style={{ color:'#ffffff' }}>Calendar Events</span></span>
    </h3>
  </div>

  <div
    style={{
      marginTop:12,
      borderRadius:16,
      background:'#141a2a',
      padding:20,
      border:'1px solid rgba(255,255,255,0.10)'
    }}
  >
    {loadingEvents ? (
      <div style={{ display:'grid', gap:12 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              height:40,
              borderRadius:10,
              background:'rgba(255,255,255,0.06)',
              animation:'pulse 1.2s ease-in-out infinite'
            }}
          />
        ))}
        {/* inline keyframes */}
        <style>{`
          @keyframes pulse { 0%,100%{opacity:.5} 50%{opacity:.9} }
        `}</style>
      </div>
    ) : eventsError ? (
      <div style={{ fontSize:14, color:'#cfe0ff' }}>{eventsError}</div>
    ) : !events.length ? (
      <div style={{ fontSize:14, color:'rgba(255,255,255,0.7)' }}>No upcoming events.</div>
    ) : (
      <ul style={{ listStyle:'none', margin:0, padding:0, display:'grid', gap:14 }}>
        {events.map((ev, i) => {
          const title = ev.event_name || ev.eventName || ev.name || ev.title || "Untitled Event";
          const when = formatEventDateTimeFromEvent(ev);
          const { day, mon } = formatEventDateLabelFromEvent(ev);
          const location = ev.location || ev.venue || "";
          const desc = cleanDesc(ev.description || "");

          const liStyle = {
            display:'flex',
            alignItems:'flex-start',
            gap:14,
            borderRadius:12,
            background:'#1a2133',
            padding:16,
            border:'1px solid rgba(255,255,255,0.10)',
            transition:'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease'
          };

          const onEnter = (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 14px 40px -18px rgba(2,8,23,.8)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
            e.currentTarget.style.background = '#1b2540';
          };
          const onLeave = (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
            e.currentTarget.style.background = '#1a2133';
          };

          const badgeBase = {
            width:48, height:48, borderRadius:10,
            display:'grid', placeItems:'center',
            background:'#2b3347',
            border:'1px solid rgba(255,255,255,0.10)',
            color:'#e6ecff'
          };

          const chip = (text) => (
            <span style={{
              display:'inline-flex', alignItems:'center', gap:6,
              padding:'3px 8px',
              borderRadius:999,
              fontSize:11,
              background:'#2b3347',
              color:'#e6ecff',
              border:'1px solid rgba(255,255,255,0.10)'
            }}>
              {text}
            </span>
          );

          return (
            <li key={i} style={liStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
              {/* Date badge */}
              {day && mon ? (
                <div style={{ ...badgeBase, flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:10, textTransform:'uppercase', opacity:.8, lineHeight:1 }}>{mon}</span>
                  <span style={{ fontSize:18, fontWeight:800, lineHeight:1, color:'#ffffff' }}>{day}</span>
                </div>
              ) : (
                <div style={badgeBase}>
                  <FaCalendarAlt style={{ opacity:.7 }} />
                </div>
              )}

              {/* Content */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                  <h4
                    title={title}
                    style={{
                      margin:0,
                      fontWeight:700,
                      fontSize:14,
                      color:'#e8f0ff',
                      lineHeight:1.2,
                      overflow:'hidden',
                      textOverflow:'ellipsis',
                      whiteSpace:'nowrap',
                      maxWidth:'78%'
                    }}
                  >
                    {title}
                  </h4>

                  <span style={{
                    display:'inline-flex', alignItems:'center', gap:6,
                    padding:'4px 8px',
                    borderRadius:999, fontSize:11,
                    background:'#262c3c',
                    color:'#e6ecff',
                    border:'1px solid rgba(255,255,255,0.10)',
                    flexShrink:0
                  }}>
                    <FaCalendarAlt style={{ opacity:.85 }} /> Event
                  </span>
                </div>

                <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:8 }}>
                  {when && chip(when)}
                  {location && chip(location)}
                </div>

                {desc && (
                  <p style={{
                    marginTop:10,
                    marginBottom:0,
                    fontSize:12.5,
                    lineHeight:1.45,
                    color:'rgba(255,255,255,0.72)',
                    display:'-webkit-box',
                    WebkitLineClamp:2,
                    WebkitBoxOrient:'vertical',
                    overflow:'hidden'
                  }}>
                    {desc}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </div>
</div>


              {/* My Projects */}
<div className="mt-8">
  <h3 className="text-lg font-semibold flex items-center gap-8" style={{display:'flex',alignItems:'center'}}>
    <FaFolderOpen style={{ color:'#7fb0ff' }} />
    <span>My <span style={{color:'#fff'}}>Projects</span></span>
  </h3>

  {loadingProjects ? (
    <div style={{
      display:'grid',
      gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))',
      gap:'16px', marginTop:'16px'
    }}>
      {Array.from({ length: 8 }).map((_, i) => <ProjectSkeletonCard key={i} />)}
    </div>
  ) : errorProjects ? (
    <div style={{
      marginTop:16, borderRadius:12, background:'#0f172a',
      padding:16, color:'#cfe0ff', border:'1px solid rgba(255,255,255,0.10)', fontSize:14
    }}>
      {errorProjects}
    </div>
  ) : projects.length === 0 ? (
    <div style={{
      marginTop:16, borderRadius:12, background:'#0f172a',
      padding:24, textAlign:'center', border:'1px solid rgba(255,255,255,0.10)'
    }}>
      <p style={{ color:'#cfe0ff' }}>No projects yet.</p>
      <button
        onClick={() => navigate("/create-project")}
        style={{
          marginTop:12, display:'inline-flex', alignItems:'center', gap:8,
          padding:'8px 14px', borderRadius:999,
          background:'#1b2438', color:'#e6efff',
          border:'1px solid rgba(255,255,255,0.10)', cursor:'pointer'
        }}
      >
        <FaPlusCircle /> Create Project
      </button>
    </div>
  ) : (
    <div style={{
      marginTop:16,
      display:'grid',
      gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))',
      gap:'16px'
    }}>
      {projects.map((p, idx) => {
        const pid   = p.id ?? idx;
        const name  = (p.name || "Untitled Project").toString();
        const desc  = (p.description || "").toString();
        const status= (p.status || "In Progress").toString();
        const phase = Number.isFinite(+p.phase_count) && +p.phase_count > 0 ? +p.phase_count : 1;
        const dateStr = p.updated_at
          ? new Date(p.updated_at).toLocaleDateString()
          : (p.created_at ? new Date(p.created_at).toLocaleDateString() : "");

        // FORCE status colors (inline)
        const st = status.toLowerCase();
        const theme = st.includes('done') || st.includes('complete')
          ? { dot:'#4cc38a', chipBg:'rgba(16,83,63,0.35)', chipText:'#baf7d0' }
          : st.includes('hold') || st.includes('block')
          ? { dot:'#f4bd50', chipBg:'rgba(92,63,12,0.35)', chipText:'#ffe3a1' }
          : st.includes('cancel')
          ? { dot:'#f2555a', chipBg:'rgba(86,20,28,0.35)', chipText:'#ffb3b6' }
          : { dot:'#6cc0ff', chipBg:'rgba(24,60,92,0.35)', chipText:'#cfe0ff' };

        const cardBase = {
          position:'relative',
          background:'#0e1729',
          backgroundImage:'radial-gradient(900px 320px at -10% -20%, rgba(63,107,179,0.14), transparent 40%)',
          border:'1px solid rgba(255,255,255,0.10)',
          borderRadius:14,
          padding:14,
          cursor:'pointer',
          boxShadow:'0 0 0 rgba(0,0,0,0)',
          transform:'translateY(0)',
          transition:'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease'
        };

        const onHover = (e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 14px 40px -18px rgba(2, 8, 23, 0.8)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
          e.currentTarget.style.backgroundColor = '#121c2f';
        };
        const onLeave = (e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 0 0 rgba(0,0,0,0)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
          e.currentTarget.style.backgroundColor = '#0e1729';
        };

        return (
          <div
            key={pid}
            role="button"
            tabIndex={0}
            onClick={() => openProject(pid)}
            onKeyDown={(e) => e.key === 'Enter' ? openProject(pid) : null}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
            style={cardBase}
          >
            {/* top row */}
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                  <h4
                    title={name}
                    style={{ fontSize:14, fontWeight:700, lineHeight:'1.2', color:'#e8f0ff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                  >
                    {name}
                  </h4>
                  <span style={{
                    padding:'0 8px', height:20, display:'inline-flex', alignItems:'center',
                    borderRadius:999, fontSize:10, letterSpacing:0.3,
                    background:'#1b2438', color:'#cfe0ff', border:'1px solid rgba(255,255,255,0.10)', flexShrink:0
                  }}>
                    Phase {phase}
                  </span>
                </div>

                <div style={{ marginTop:6, display:'flex', alignItems:'center', gap:8, fontSize:11, color:'rgba(255,255,255,0.65)' }}>
                  <span style={{
                    display:'inline-flex', alignItems:'center', gap:6,
                    padding:'0 8px', height:20, borderRadius:999,
                    background: theme.chipBg, color: theme.chipText,
                    border:'1px solid rgba(255,255,255,0.10)'
                  }}>
                    <span style={{
                      width:6, height:6, borderRadius:999, background: theme.dot,
                      boxShadow:`0 0 8px ${theme.dot}A0`
                    }}/>
                    <span style={{ letterSpacing:0.3 }}>{status}</span>
                  </span>
                  {dateStr && <span>{p.updated_at ? 'Updated' : 'Created'} {dateStr}</span>}
                </div>
              </div>

              {/* compact chevron */}
              <div style={{
                width:32, height:32, display:'grid', placeItems:'center',
                borderRadius:999, background:'#1b2438', border:'1px solid rgba(255,255,255,0.10)'
              }}>
                <FaChevronRight style={{ color:'rgba(255,255,255,0.85)' }} />
              </div>
            </div>

            {/* description */}
            {desc && (
              <p style={{
                marginTop:10, fontSize:12.5, lineHeight:'1.45',
                color:'rgba(255,255,255,0.72)',
                display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'
              }}>
                {desc}
              </p>
            )}
          </div>
        );
      })}
    </div>
  )}
</div>

            </div>

            {/* Divider (only on xl) */}
            <div className="hidden xl:block col-span-12 xl:col-span-1">
              <div className="h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent mx-auto" />
            </div>

            {/* RIGHT RAIL */}
            <div className="col-span-12 xl:col-span-4">
              {/* What's New (Portal 4.0) */}
              {/* What's New — forced inline CSS, professional dark navy UI */}
<section
  style={{
    borderRadius: 16,
    background: '#141a2a',
    padding: 20,
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 10px 40px -22px rgba(0,0,0,.65)'
  }}
>
  {/* Heading */}
  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
    <span style={{ color:'#6d6aff', fontSize:18 }}></span>
    <h3 style={{ margin:0, fontWeight:600, color:'#cfe0ff' }}>
      What’s <span style={{ color:'#ffffff' }}>New</span>
    </h3>
  </div>

  {/* Version badge */}
  <div style={{ marginTop:4 }}>
    <span
      style={{
        display:'inline-flex',
        alignItems:'center',
        gap:6,
        padding:'4px 10px',
        borderRadius:999,
        fontSize:12,
        letterSpacing:.3,
        background:'#1b2438',
        color:'#e6ecff',
        border:'1px solid rgba(255,255,255,0.10)'
      }}
    >
      Version 4.0
    </span>
  </div>

  {/* Highlights */}
  <ul style={{ margin:12, marginTop:14, padding:0, listStyle:'none', display:'grid', gap:10 }}>
    {[
      'Clean navy-blue interface with a polished, modern look.',
      'Smarter navigation that makes projects and updates easier to access.',
      'Personalized dashboard designed to keep you engaged every day.',
      'Stronger reliability with smooth performance across all devices.'
    ].map((text, i) => (
      <li key={i} style={{ display:'flex', alignItems:'start', gap:10, color:'rgba(255,255,255,0.80)', fontSize:14, lineHeight:1.45 }}>
        <span
          style={{
            marginTop:6,
            width:7, height:7, borderRadius:999,
            background:'#6d6aff',
            boxShadow:'0 0 10px rgba(109,106,255,.55)',
            flex:'0 0 auto'
          }}
        />
        <span>{text}</span>
      </li>
    ))}
  </ul>
</section>


              {/* Upcoming Projects */}
              <section className="mt-6 rounded-2xl bg-[#141a2a] p-5 ring-1 ring-white/10 shadow-[0_10px_40px_-22px_rgba(0,0,0,.65)]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#6d6aff] text-lg">🗓️</span>
                  <h3 className="font-semibold">Upcoming <span className="text-white">Projects</span></h3>
                </div>

                {/* Simple vertical timeline */}
                <div className="mt-4 relative pl-6">
                  <span className="absolute left-[9px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#6d6aff] to-transparent" />
                  <div className="relative mb-3">
                    <span className="absolute -left-1 top-[2px] h-3 w-3 bg-[#6d6aff] rounded-full" />
                    <div className="text-white/80">No Upcoming Projects</div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
